import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Table, Form, Input, Select, Tag, Space, Divider,
  Typography, Modal, Radio, TreeSelect, Drawer, TimePicker, Alert, message
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, MoreOutlined } from '@ant-design/icons';
import Dropdown from 'antd/es/dropdown';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PRODUCTS = [
  { value: 'PROD_Q1', label: '智能摄像机Q1', source: 'self', type: 'machine' },
  { value: 'PROD_P1', label: '云台机P1', source: 'self', type: 'machine' },
  { value: 'PROD_F1', label: '烟雾报警器F1', source: 'ecosystem', vendorName: '深圳芯视界科技有限公司', vendorId: 'ENT_V01', planName: '智能烟感方案', planId: 'P_ECO_005', type: 'machine' },
  { value: 'PROD_S1', label: '智能门锁S1', source: 'self', type: 'machine' },
  { value: 'P_ECO_001', label: '智能云台摄像机方案（方案产品）', source: 'self', type: 'plan' },
];

const OTA_FW_OPTIONS = [
  { value: 'fw_main_v3.0', label: '主控固件 / V3.0.0', finalized: true },
  { value: 'fw_main_v3.1', label: '主控固件 / V3.1.0', finalized: false },
  { value: 'fw_wifi_v2.0', label: 'WiFi模组固件 / V2.0.0', finalized: true },
  { value: 'fw_mcu_v2.0', label: 'MCU固件 / V2.0.0', finalized: true },
  { value: 'fw_mcu_v2.1', label: 'MCU固件 / V2.1.0', finalized: false },
];

const BASE_VERSION_OPTIONS = [
  { value: 'fw_main_v1.0', label: '主控固件 / V1.0.0' },
  { value: 'fw_main_v2.0', label: '主控固件 / V2.0.0' },
  { value: 'fw_main_v2.5', label: '主控固件 / V2.5.0' },
  { value: 'fw_wifi_v1.0', label: 'WiFi模组固件 / V1.0.0' },
  { value: 'fw_wifi_v1.2', label: 'WiFi模组固件 / V1.2.0' },
  { value: 'fw_mcu_v1.0', label: 'MCU固件 / V1.0.0' },
  { value: 'fw_mcu_v1.1', label: 'MCU固件 / V1.1.0' },
];

const REGION_TREE = [
  { title: '中国', value: 'CN', children: [
    { title: '广东省', value: 'CN_GD', children: [
      { title: '深圳市', value: 'CN_GD_SZ' }, { title: '广州市', value: 'CN_GD_GZ' }, { title: '东莞市', value: 'CN_GD_DG' },
    ]},
    { title: '浙江省', value: 'CN_ZJ', children: [
      { title: '杭州市', value: 'CN_ZJ_HZ' }, { title: '宁波市', value: 'CN_ZJ_NB' },
    ]},
    { title: '北京市', value: 'CN_BJ' },
    { title: '上海市', value: 'CN_SH' },
  ]},
  { title: '美国', value: 'US' },
  { title: '日本', value: 'JP' },
  { title: '韩国', value: 'KR' },
  { title: '德国', value: 'DE' },
  { title: '英国', value: 'GB' },
  { title: '巴西', value: 'BR' },
  { title: '印度', value: 'IN' },
];

const STATUS_MAP = {
  pending: { text: '待发布', color: 'default' },
  publishing: { text: '发布中', color: 'processing' },
  paused: { text: '已暂停', color: 'warning' },
  terminated: { text: '已终止', color: 'error' },
};

const mockData = [
  { key: '1', productName: '智能摄像机Q1', productId: 'PROD_Q1', source: 'self', vendorName: null, fwName: '主控固件', fwVersion: 'V3.0.0', coverDevices: 5000, pushed: 3200, upgraded: 2800, status: 'publishing' },
  { key: '2', productName: '云台机P1', productId: 'PROD_P1', source: 'self', vendorName: null, fwName: 'WiFi模组固件', fwVersion: 'V2.0.0', coverDevices: 1200, pushed: 0, upgraded: 0, status: 'pending' },
  { key: '3', productName: '智能摄像机Q1', productId: 'PROD_Q1', source: 'self', vendorName: null, fwName: 'MCU固件', fwVersion: 'V2.1.0', coverDevices: 3000, pushed: 3000, upgraded: 2950, status: 'paused' },
  { key: '4', productName: '烟雾报警器F1', productId: 'PROD_F1', source: 'ecosystem', vendorName: '深圳芯视界科技有限公司', vendorId: 'ENT_V01', planName: '智能烟感方案', planId: 'P_ECO_005', fwName: '主控固件', fwVersion: 'V3.1.0', coverDevices: 800, pushed: 800, upgraded: 800, status: 'terminated' },
];

const FirmwareOta = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [createVisible, setCreateVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [baseVersionType, setBaseVersionType] = useState('all');
  const [upgradeMode, setUpgradeMode] = useState('app');
  const [deviceScope, setDeviceScope] = useState('all');

  const confirmAction = (title, content, onOk) => {
    Modal.confirm({ title, content, okText: '确认', cancelText: '取消', onOk });
  };

  const renderActions = (record) => {
    const { status } = record;
    const detailBtn = { key: 'detail', label: '升级详情', onClick: () => navigate(`/device/firmware/ota/upgradeNum?id=${record.key}`) };

    if (status === 'pending') {
      return (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" onClick={() => confirmAction('发布任务', '确认发布该OTA升级任务？', () => message.success('已发布'))}>发布任务</Button>
          <Dropdown menu={{ items: [detailBtn, { key: 'delete', label: '删除', danger: true, onClick: () => confirmAction('删除任务', '确认删除该任务？删除后不可恢复。', () => message.success('已删除')) }] }}>
            <Button type="link" size="small" icon={<MoreOutlined />}>更多</Button>
          </Dropdown>
        </Space>
      );
    }
    if (status === 'publishing') {
      return (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" onClick={detailBtn.onClick}>升级详情</Button>
          <Dropdown menu={{ items: [
            { key: 'pause', label: '暂停', onClick: () => confirmAction('暂停任务', '确认暂停该OTA升级任务？', () => message.success('已暂停')) },
            { key: 'terminate', label: '终止任务', danger: true, onClick: () => confirmAction('终止任务', '终止后不可恢复，确认终止？', () => message.success('已终止')) },
          ] }}>
            <Button type="link" size="small" icon={<MoreOutlined />}>更多</Button>
          </Dropdown>
        </Space>
      );
    }
    if (status === 'paused') {
      return (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" onClick={() => confirmAction('继续发布', '确认继续发布该OTA升级任务？', () => message.success('已继续发布'))}>继续发布</Button>
          <Dropdown menu={{ items: [detailBtn, { key: 'terminate', label: '终止任务', danger: true, onClick: () => confirmAction('终止任务', '终止后不可恢复，确认终止？', () => message.success('已终止')) }] }}>
            <Button type="link" size="small" icon={<MoreOutlined />}>更多</Button>
          </Dropdown>
        </Space>
      );
    }
    // terminated
    return <Button type="link" size="small" onClick={detailBtn.onClick}>升级详情</Button>;
  };

  const columns = [
    {
      title: '产品信息', key: 'product', width: 180,
      render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.productName} {r.source === 'ecosystem' && <Tag color="blue" style={{ marginLeft: 4, fontSize: 11 }}>方案派生</Tag>}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>),
    },
    { title: '升级固件', key: 'fw', width: 200, render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.fwName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.fwVersion}</Text></div>) },
    {
      title: '方案产品', key: 'planProduct', width: 200,
      render: (_, r) => {
        if (!r.planName) {
          return <span style={{ color: '#ccc' }}>-</span>;
        }
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{r.planName}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.planId}</Text>
          </div>
        );
      },
    },
    {
      title: '固件来源', key: 'fwSource', width: 180,
      render: (_, r) => {
        if (r.source === 'ecosystem') return <div><div style={{ fontWeight: 500, color: '#1890ff' }}>{r.vendorName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.vendorId || ''}</Text></div>;
        return <Text type="secondary">自有固件</Text>;
      },
    },
    { title: '覆盖设备数', dataIndex: 'coverDevices', width: 110 },
    { title: '已推送设备', dataIndex: 'pushed', width: 110 },
    { title: '已升级设备', dataIndex: 'upgraded', width: 110 },
    {
      title: '任务状态', dataIndex: 'status', width: 100,
      render: (v) => { const s = STATUS_MAP[v]; return <Tag color={s.color}>{s.text}</Tag>; },
    },
    { title: '操作', key: 'action', width: 200, fixed: 'right', render: (_, r) => renderActions(r) },
  ];

  const handleCreate = async () => {
    try {
      await createForm.validateFields();
      message.success('OTA任务已创建');
      setCreateVisible(false);
    } catch { /* validation */ }
  };

  const openCreate = () => {
    createForm.resetFields();
    setSelectedProduct(null); setBaseVersionType('all'); setUpgradeMode('app'); setDeviceScope('all');
    setCreateVisible(true);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Title level={4}>固件OTA</Title></div>

      {/* 查询条件 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12 }} bodyStyle={{ padding: '12px 0' }}>
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="prodType" initialValue="name" noStyle>
                <Select style={{ width: 100 }}>
                  <Option value="name">产品名称</Option>
                  <Option value="id">产品ID</Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(p, c) => p.prodType !== c.prodType} noStyle>
                {({ getFieldValue }) => getFieldValue('prodType') === 'id'
                  ? <Form.Item name="prodKeyword" noStyle><Input placeholder="产品ID" allowClear style={{ width: 220 }} /></Form.Item>
                  : <Form.Item name="prodKeyword" noStyle><Select placeholder="请选择产品" allowClear showSearch optionFilterProp="label" style={{ width: 220 }} options={PRODUCTS} /></Form.Item>
                }
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 列表 */}
      <Card bordered={false}>
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>创建发布任务</Button>
        </div>
        <Table columns={columns} dataSource={mockData} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 创建发布任务 */}
      <Drawer title="创建发布任务" width={700} open={createVisible} onClose={() => setCreateVisible(false)} destroyOnClose
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button onClick={() => setCreateVisible(false)}>取消</Button><Button type="primary" onClick={handleCreate}>创建任务</Button></div>}
      >
        <Form form={createForm} layout="vertical" initialValues={{ baseVersionType: 'all', upgradeMode: 'app', deviceScope: 'all', defaultLang: 'zh' }}>
          <Form.Item name="product" label="整机产品" rules={[{ required: true, message: '请选择整机产品' }]}>
            <Select
              placeholder="请选择当前企业下的整机产品"
              showSearch
              optionFilterProp="label"
              options={PRODUCTS.filter(p => p.type !== 'plan')}
              onChange={(v) => setSelectedProduct(v)}
            />
          </Form.Item>
          <div style={{ marginTop: -8, marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>仅整机产品可创建 OTA 任务，方案产品不支持 OTA。</Text>
          </div>
          {selectedProduct && (() => {
            const p = PRODUCTS.find(x => x.value === selectedProduct);
            return (
              <div style={{ marginTop: -8, marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>产品ID：{selectedProduct}</Text>
                <div style={{ marginTop: 4 }}>
                  {p?.source === 'ecosystem' ? (
                    <>
                      <Tag color="blue">方案派生</Tag>
                      <div style={{ marginTop: 4, fontSize: 12 }}>
                        方案产品：{p.planName || '-'}（{p.planId || '-'}）
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>固件由 {p.vendorName} 提供</Text>
                    </>
                  ) : (
                    <Tag color="green">自研整机</Tag>
                  )}
                </div>
              </div>
            );
          })()}

          <Form.Item name="otaFw" label="选择OTA固件" rules={[{ required: true, message: '请选择固件' }]}>
            <Select showSearch optionFilterProp="label" placeholder="请选择固件" style={{ width: '100%' }}>
              {OTA_FW_OPTIONS.map(fw => (
                <Select.Option key={fw.value} value={fw.value} label={fw.label} disabled={!fw.finalized}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{fw.label}</span>
                    <Tag color={fw.finalized ? 'green' : 'default'} style={{ marginRight: 0, fontSize: 11 }}>{fw.finalized ? '已定版' : '未定版'}</Tag>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Divider orientation="left" plain>版本范围</Divider>
          <Form.Item name="baseVersionType" label="基于固件版本">
            <Radio.Group onChange={(e) => setBaseVersionType(e.target.value)}>
              <Radio value="all">不限版本</Radio>
              <Radio value="specify">指定版本</Radio>
            </Radio.Group>
          </Form.Item>
          {baseVersionType === 'specify' && (
            <Form.Item name="baseVersions" label="指定版本" rules={[{ required: true, message: '请选择版本' }]}>
              <Select mode="multiple" showSearch optionFilterProp="label" options={BASE_VERSION_OPTIONS} placeholder="请选择版本" style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Divider orientation="left" plain>升级策略</Divider>
          <Form.Item name="upgradeMode" label="升级方式">
            <Radio.Group onChange={(e) => setUpgradeMode(e.target.value)}>
              <Radio value="app">App提醒升级</Radio>
              <Radio value="silent">静默升级</Radio>
            </Radio.Group>
          </Form.Item>
          {upgradeMode === 'silent' && (
            <>
              <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 6 }}
                message="系统会延迟半小时推送，如设置为01:15-03:00，则系统推送时间为01:45开始推送。升级时间会按照设备所在区域当地时间升级，如设备在美国则以美国的01:45开始推送，设备在中国则以北京时间01:45开始推送。" />
              <Form.Item name="upgradeTime" label="升级时间" rules={[{ required: true, message: '请选择升级时间' }]}>
                <TimePicker.RangePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          <Divider orientation="left" plain>设备范围</Divider>
          <Form.Item name="deviceScope" label="待升级设备">
            <Radio.Group onChange={(e) => setDeviceScope(e.target.value)}>
              <Radio value="all">全部设备</Radio>
              <Radio value="region">指定区域</Radio>
              <Radio value="device">指定设备</Radio>
            </Radio.Group>
          </Form.Item>
          {deviceScope === 'region' && (
            <Form.Item name="regions" label="选择区域" rules={[{ required: true, message: '请选择区域' }]}>
              <TreeSelect treeData={REGION_TREE} treeCheckable placeholder="请选择区域" style={{ width: '100%' }} showCheckedStrategy={TreeSelect.SHOW_PARENT} />
            </Form.Item>
          )}
          {deviceScope === 'device' && (
            <Form.Item name="deviceInput" label={
              <Form.Item name="deviceIdType" initialValue="license" noStyle>
                <Radio.Group size="small">
                  <Radio.Button value="license">License</Radio.Button>
                  <Radio.Button value="did">DID</Radio.Button>
                </Radio.Group>
              </Form.Item>
            } rules={[{ required: true, message: '请输入设备标识' }]}>
              <TextArea rows={6} placeholder="每行一个" />
            </Form.Item>
          )}

          <Divider orientation="left" plain>升级内容</Divider>
          <Form.Item name="defaultLang" label="升级内容（中文）">
            <Input.TextArea rows={3} placeholder="请输入升级内容描述" style={{ borderRadius: 6 }} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default FirmwareOta;
